<script>
import axios from "axios";
import Swal from "sweetalert2";
import { mapGetters } from "vuex";
import {
  getMostRecentAcademicSession,
  sortAcademicSessionsDescending,
} from "@/utils/academicSessionUtils";
import ApplicationEditModal from "@/components/modules/ApplicationEditModal.vue";
import ApplicationProgrammeModal from "@/components/modules/ApplicationProgrammeModal.vue";
import ApplicationDocumentsModal from "@/components/modules/ApplicationDocumentsModal.vue";

const STATUS_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "submitted", label: "Submitted" },
  { value: "admission_offered", label: "Admitted" },
  { value: "rejected", label: "Rejected" },
  { value: "withdrawn", label: "Withdrawn" },
  { value: "enrolled", label: "Enrolled" },
];

const STAGE_OPTIONS = [
  { value: "portal_fee", label: "Portal Fee" },
  { value: "application_form", label: "Application Form" },
  { value: "admission", label: "Admission" },
  { value: "acceptance_fee", label: "Acceptance Fee" },
  { value: "clearance", label: "Clearance" },
  { value: "administrative_fee", label: "Administrative Fee" },
  { value: "school_fee", label: "School Fee" },
  { value: "completed", label: "Completed" },
  { value: "rejected", label: "Rejected" },
  { value: "withdrawn", label: "Withdrawn" },
];

export default {
  name: "ApplicationsMgt",
  components: {
    ApplicationEditModal,
    ApplicationProgrammeModal,
    ApplicationDocumentsModal,
  },
  data() {
    return {
      applications: [],
      academicSessions: [],
      programOfferings: [],
      loading: false,
      refreshing: false,
      exporting: false,
      detailsLoading: false,
      documentLoadingId: null,
      selectedApplication: null,
      actionApplication: null,
      actionMode: null,
      actionLoading: false,
      searchQuery: "",
      searchTimer: null,
      initialized: false,
      pagination: {
        currentPage: 1,
        itemsPerPage: 10,
        totalItems: 0,
        totalPages: 1,
      },
      filters: {
        session: "",
        status: "",
        stage: "",
        program: "",
        dateRange: { start: "", end: "" },
      },
      sortBy: "createdAt",
      sortOrder: "desc",
      statusOptions: STATUS_OPTIONS,
      stageOptions: STAGE_OPTIONS,
    };
  },
  computed: {
    ...mapGetters(["hasPermission"]),
    pageRange() {
      const maximum = 5;
      const total = this.pagination.totalPages;
      const current = this.pagination.currentPage;
      if (total <= maximum) {
        return Array.from({ length: total }, (_, index) => index + 1);
      }
      let start = Math.max(1, current - Math.floor(maximum / 2));
      const end = Math.min(total, start + maximum - 1);
      if (end - start < maximum - 1) start = Math.max(1, end - maximum + 1);
      return Array.from(
        { length: end - start + 1 },
        (_, index) => start + index
      );
    },
    paginationInfo() {
      const start = this.pagination.totalItems
        ? (this.pagination.currentPage - 1) * this.pagination.itemsPerPage + 1
        : 0;
      return {
        start,
        end: Math.min(
          this.pagination.currentPage * this.pagination.itemsPerPage,
          this.pagination.totalItems
        ),
        total: this.pagination.totalItems,
      };
    },
    availableProgramOfferings() {
      return this.programOfferings
        .filter(
          (offering) =>
            offering.active !== false && offering.program?.active !== false
        )
        .map((offering) => ({
          _id: offering._id,
          name: this.formatProgramOffering(offering),
        }));
    },
    sortedAcademicSessions() {
      return sortAcademicSessionsDescending(this.academicSessions);
    },
    selectedSessionLabel() {
      return (
        this.academicSessions.find(
          (session) => session._id === this.filters.session
        )?.sessionYear || "All sessions"
      );
    },
    progressStages() {
      const progress = this.selectedApplication?.progress || {};
      return [
        ["Registration", progress.registrationCompleted],
        ["Portal Fee", progress.portalFeePaid],
        ["Application Form", progress.applicationFormSubmitted],
        ["Admission", progress.admissionOffered],
        ["Acceptance Fee", progress.acceptanceFeePaid],
        ["Clearance", progress.clearanceCompleted],
        ["Administrative Fee", progress.administrativeFeePaid],
        ["School Fee", progress.schoolFeePaid],
      ].map(([label, completed]) => ({ label, completed: completed === true }));
    },
    progressPercentage() {
      if (!this.progressStages.length) return 0;
      return Math.round(
        (this.progressStages.filter((stage) => stage.completed).length /
          this.progressStages.length) *
          100
      );
    },
  },
  watch: {
    searchQuery() {
      if (!this.initialized) return;
      window.clearTimeout(this.searchTimer);
      this.searchTimer = window.setTimeout(
        () => this.fetchApplications(1),
        350
      );
    },
    "pagination.itemsPerPage"() {
      if (this.initialized) this.fetchApplications(1);
    },
    "filters.session"() {
      if (this.initialized) this.fetchApplications(1);
    },
    "filters.status"() {
      if (this.initialized) this.fetchApplications(1);
    },
    "filters.stage"() {
      if (this.initialized) this.fetchApplications(1);
    },
    "filters.program"() {
      if (this.initialized) this.fetchApplications(1);
    },
    "filters.dateRange.start"() {
      if (this.initialized) this.fetchApplications(1);
    },
    "filters.dateRange.end"() {
      if (this.initialized) this.fetchApplications(1);
    },
  },
  async mounted() {
    await this.fetchInitialData();
  },
  beforeUnmount() {
    window.clearTimeout(this.searchTimer);
  },
  methods: {
    requestParams(page = 1, limit = this.pagination.itemsPerPage) {
      return {
        page,
        limit,
        search: this.searchQuery.trim() || undefined,
        entrySessionId: this.filters.session || undefined,
        status: this.filters.status || undefined,
        stage: this.filters.stage || undefined,
        programOfferingId: this.filters.program || undefined,
        startDate: this.filters.dateRange.start || undefined,
        endDate: this.filters.dateRange.end || undefined,
        sortBy: this.sortBy,
        sortOrder: this.sortOrder,
      };
    },
    async fetchInitialData() {
      this.loading = true;
      try {
        const [sessionsResponse, offeringsResponse] = await Promise.all([
          axios.get(`${process.env.VUE_APP_API_BASE_URL}/academic-sessions`),
          axios.get(`${process.env.VUE_APP_API_BASE_URL}/program-offerings`),
        ]);
        this.academicSessions = Array.isArray(sessionsResponse.data)
          ? sessionsResponse.data
          : [];
        this.programOfferings = Array.isArray(offeringsResponse.data)
          ? offeringsResponse.data
          : [];
        const mostRecentSession = getMostRecentAcademicSession(
          this.academicSessions
        );
        this.filters.session = mostRecentSession?._id || "";
        this.initialized = true;
        await this.fetchApplications(1);
      } catch (error) {
        this.initialized = true;
        this.showError(error, "Failed to load applications management");
      } finally {
        this.loading = false;
      }
    },
    async fetchApplications(page = this.pagination.currentPage) {
      this.loading = true;
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/applications`,
          { params: this.requestParams(page) }
        );
        const data = response.data?.data || {};
        this.applications = data.applications || [];
        this.pagination.currentPage = data.pagination?.page || page;
        this.pagination.totalItems = data.pagination?.total || 0;
        this.pagination.totalPages = data.pagination?.pages || 1;
      } catch (error) {
        this.showError(error, "Failed to fetch applications");
      } finally {
        this.loading = false;
      }
    },
    async refreshApplications() {
      this.refreshing = true;
      await this.fetchApplications(this.pagination.currentPage);
      this.refreshing = false;
    },
    clearFilters() {
      this.initialized = false;
      window.clearTimeout(this.searchTimer);
      this.searchQuery = "";
      this.filters.status = "";
      this.filters.stage = "";
      this.filters.program = "";
      this.filters.dateRange = { start: "", end: "" };
      const mostRecentSession = getMostRecentAcademicSession(
        this.academicSessions
      );
      this.filters.session = mostRecentSession?._id || "";
      this.$nextTick(() => {
        this.initialized = true;
        this.fetchApplications(1);
      });
    },
    fullName(application) {
      const user = application?.userId || {};
      return (
        [user.firstName, user.otherName, user.lastName]
          .filter(Boolean)
          .join(" ") || "N/A"
      );
    },
    formatProgramOffering(offering) {
      if (!offering) return "N/A";
      const type = offering.structure?.type?.type || "";
      const mode =
        offering.structure?.mode?.description ||
        offering.structure?.mode?.mode ||
        "";
      const program = offering.program?.name || "";
      return [type, mode, program].filter(Boolean).join(" ") || "N/A";
    },
    formatDate(value, includeTime = false) {
      if (!value) return "N/A";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "N/A";
      return includeTime ? date.toLocaleString() : date.toLocaleDateString();
    },
    formatAddress(address) {
      if (!address) return "N/A";
      if (typeof address === "string") return address;
      return (
        [
          address.line1,
          address.line2,
          address.city,
          address.state,
          address.country,
        ]
          .filter(Boolean)
          .join(", ") || "N/A"
      );
    },
    getStatusLabel(status) {
      return (
        this.statusOptions.find((option) => option.value === status)?.label ||
        status ||
        "N/A"
      );
    },
    getStatusBadgeColor(status) {
      return (
        {
          draft: "secondary",
          submitted: "warning",
          admission_offered: "success",
          rejected: "danger",
          withdrawn: "dark",
          enrolled: "primary",
        }[status] || "secondary"
      );
    },
    getCurrentStage(application) {
      const progress = application?.progress || {};
      const currentStage = progress.currentStage;
      const trackerStage = (progress.trackerStages || []).find(
        (stage) => stage.key === currentStage
      );
      if (trackerStage?.label) return trackerStage.label;
      return (
        {
          completed: "Completed",
          rejected: "Rejected",
          withdrawn: "Withdrawn",
        }[currentStage] || "Not available"
      );
    },
    getCurrentStageBadgeColor(application) {
      return (
        {
          completed: "success",
          rejected: "danger",
          withdrawn: "dark",
        }[application?.progress?.currentStage] || "secondary"
      );
    },
    getClearanceLabel(status) {
      return (
        {
          not_ready: "Not ready",
          pending: "Pending",
          cleared: "Cleared",
          denied: "Denied",
        }[status] ||
        status ||
        "Not ready"
      );
    },
    canAdmit(application) {
      return (
        application.status === "submitted" &&
        application.progress?.portalFeePaid === true &&
        this.hasPermission("applications", "approve")
      );
    },
    canReject(application) {
      return (
        !["rejected", "withdrawn", "enrolled"].includes(application.status) &&
        application.progress?.acceptanceFeePaid !== true &&
        this.hasPermission("applications", "reject")
      );
    },
    canReopen(application) {
      return (
        application.status === "rejected" &&
        this.hasPermission("applications", "reopen")
      );
    },
    canDecideClearance(application) {
      return (
        application.status === "admission_offered" &&
        application.progress?.acceptanceFeePaid === true &&
        application.clearance?.status !== "cleared" &&
        this.hasPermission("applications", "clear")
      );
    },
    canEditApplication(application) {
      return (
        ["draft", "submitted", "admission_offered"].includes(
          application.status
        ) && this.hasPermission("applications", "edit")
      );
    },
    canChangeProgramme(application) {
      return (
        ["draft", "submitted"].includes(application.status) &&
        !application.admission?.acceptedAt &&
        this.hasPermission("applications", "change-programme")
      );
    },
    canManageDocuments(application) {
      return (
        !["rejected", "withdrawn"].includes(application.status) &&
        this.hasPermission("applications", "manage-documents")
      );
    },
    hasAnyRowActions(application) {
      return (
        this.canEditApplication(application) ||
        this.canChangeProgramme(application) ||
        this.canManageDocuments(application) ||
        this.canAdmit(application) ||
        this.canReject(application) ||
        this.canReopen(application) ||
        this.canDecideClearance(application)
      );
    },
    async openApplicationAction(application, mode) {
      this.actionLoading = true;
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/applications/${application._id}`
        );
        this.actionApplication = response.data.data;
        this.actionMode = mode;
      } catch (error) {
        this.showError(error, "Failed to load application details");
      } finally {
        this.actionLoading = false;
      }
    },
    closeApplicationAction() {
      this.actionMode = null;
      this.actionApplication = null;
    },
    async handleApplicationActionSaved(options = {}) {
      const applicationId = this.actionApplication?._id;
      const mode = this.actionMode;
      await this.fetchApplications(this.pagination.currentPage);
      if (
        applicationId &&
        (options.keepOpen || this.selectedApplication?._id === applicationId)
      ) {
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/applications/${applicationId}`
        );
        if (options.keepOpen) this.actionApplication = response.data.data;
        if (this.selectedApplication?._id === applicationId) {
          this.selectedApplication = response.data.data;
        }
      }
      if (!options.keepOpen) {
        this.closeApplicationAction();
      }
      await Swal.fire(
        "Success",
        mode === "programme"
          ? "Programme changed successfully"
          : mode === "documents"
          ? "Document action completed"
          : "Application corrected successfully",
        "success"
      );
    },
    async viewApplicationDetails(application) {
      this.detailsLoading = true;
      this.selectedApplication = null;
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/applications/${application._id}`
        );
        this.selectedApplication = response.data.data;
      } catch (error) {
        this.showError(error, "Failed to load application details");
      } finally {
        this.detailsLoading = false;
      }
    },
    closeDetails() {
      this.selectedApplication = null;
    },
    async performAction(application, endpoint, options) {
      const confirmation = await Swal.fire({
        title: options.title,
        text: options.text,
        icon: options.icon || "question",
        input: options.needsReason ? "textarea" : undefined,
        inputLabel: options.needsReason ? "Reason" : undefined,
        inputPlaceholder: options.needsReason ? "Enter the reason" : undefined,
        inputValidator: options.needsReason
          ? (value) =>
              typeof value !== "string" || !value.trim()
                ? "A reason is required"
                : undefined
          : undefined,
        showCancelButton: true,
        confirmButtonText: options.confirmText || "Confirm",
      });
      if (!confirmation.isConfirmed) return;
      const reason =
        typeof confirmation.value === "string"
          ? confirmation.value.trim()
          : undefined;
      try {
        await axios.post(
          `${process.env.VUE_APP_API_BASE_URL}/applications/${application._id}/${endpoint}`,
          options.needsReason ? { reason } : {}
        );
        this.closeDetails();
        await this.fetchApplications(this.pagination.currentPage);
        await Swal.fire("Success", options.successMessage, "success");
      } catch (error) {
        this.showError(error, "Unable to update the application");
      }
    },
    admitApplicant(application) {
      return this.performAction(application, "admission", {
        title: "Admit Applicant?",
        text: "This will offer admission to the applicant.",
        confirmText: "Yes, Admit",
        successMessage: "Applicant admitted successfully",
      });
    },
    rejectApplicant(application) {
      return this.performAction(application, "reject", {
        title: "Reject Applicant?",
        text: "This will reject the application.",
        icon: "warning",
        needsReason: true,
        confirmText: "Yes, Reject",
        successMessage: "Application rejected successfully",
      });
    },
    reopenApplication(application) {
      return this.performAction(application, "reopen", {
        title: "Reopen Application?",
        text: "This will return the rejected application for review.",
        needsReason: true,
        confirmText: "Yes, Reopen",
        successMessage: "Application reopened successfully",
      });
    },
    clearApplicant(application) {
      return this.performAction(application, "clearance/approve", {
        title: "Clear Applicant?",
        text: "This will approve the applicant's school clearance.",
        confirmText: "Yes, Clear",
        successMessage: "Applicant cleared successfully",
      });
    },
    denyClearance(application) {
      return this.performAction(application, "clearance/deny", {
        title: "Deny Clearance?",
        text: "This will deny the applicant's school clearance.",
        icon: "warning",
        needsReason: true,
        confirmText: "Yes, Deny",
        successMessage: "Clearance denied successfully",
      });
    },
    documentLabel(document) {
      const labels = {
        profile_image: "Profile Image",
        jamb_result: "JAMB Result",
        birth_certificate: "Birth Certificate",
        o_level_result: `O-Level Result${
          document.sittingNumber ? ` (Sitting ${document.sittingNumber})` : ""
        }`,
      };
      return labels[document.documentType] || document.originalName;
    },
    async openDocument(document) {
      this.documentLoadingId = document._id;
      try {
        const contentUrl = String(document.contentUrl || "").replace(
          /^\/api\//,
          "/"
        );
        const response = await axios.get(contentUrl, {
          responseType: "blob",
        });
        const url = URL.createObjectURL(response.data);
        window.open(url, "_blank", "noopener,noreferrer");
        window.setTimeout(() => URL.revokeObjectURL(url), 60000);
      } catch (error) {
        this.showError(error, "Failed to open the document");
      } finally {
        this.documentLoadingId = null;
      }
    },
    paymentName(payment) {
      return (
        payment.payment?.payment?.description ||
        payment.payment?.payment?.name ||
        "Payment"
      );
    },
    async fetchAllFilteredApplications() {
      const first = await axios.get(
        `${process.env.VUE_APP_API_BASE_URL}/applications`,
        { params: this.requestParams(1, 200) }
      );
      const result = [...(first.data.data.applications || [])];
      const pages = first.data.data.pagination?.pages || 1;
      for (let page = 2; page <= pages; page += 1) {
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/applications`,
          { params: this.requestParams(page, 200) }
        );
        result.push(...(response.data.data.applications || []));
      }
      return result;
    },
    exportRow(application) {
      return [
        application.applicationNumber || "",
        this.fullName(application),
        application.userId?.email || "",
        application.userId?.phone || "",
        this.formatProgramOffering(application.programOfferingId),
        application.entrySessionId?.sessionYear || "",
        this.getStatusLabel(application.status),
        this.getCurrentStage(application),
        this.getClearanceLabel(application.clearance?.status),
        this.formatDate(application.submittedAt),
        this.formatDate(application.updatedAt),
      ];
    },
    async exportAsCSV() {
      this.exporting = true;
      try {
        const applications = await this.fetchAllFilteredApplications();
        if (!applications.length) {
          await Swal.fire(
            "No Data",
            "No applications match the current filters",
            "warning"
          );
          return;
        }
        const headers = [
          "Application No.",
          "Full Name",
          "Email",
          "Phone",
          "Programme",
          "Session",
          "Status",
          "Stage",
          "Clearance",
          "Submitted",
          "Last Updated",
        ];
        const escapeCell = (cell) =>
          `"${String(cell ?? "").replace(/"/g, '""')}"`;
        const content = [
          headers,
          ...applications.map((application) => this.exportRow(application)),
        ]
          .map((row) => row.map(escapeCell).join(","))
          .join("\n");
        const url = URL.createObjectURL(
          new Blob([content], { type: "text/csv;charset=utf-8" })
        );
        const link = document.createElement("a");
        link.href = url;
        link.download = `applications_${new Date()
          .toISOString()
          .slice(0, 10)}.csv`;
        link.click();
        URL.revokeObjectURL(url);
        await Swal.fire(
          "Success",
          `Exported ${applications.length} applications`,
          "success"
        );
      } catch (error) {
        this.showError(error, "Failed to export applications");
      } finally {
        this.exporting = false;
      }
    },
    async exportAsPDF() {
      this.exporting = true;
      try {
        const applications = await this.fetchAllFilteredApplications();
        if (!applications.length) {
          await Swal.fire(
            "No Data",
            "No applications match the current filters",
            "warning"
          );
          return;
        }
        const jsPDF = (await import("jspdf")).default;
        await import("jspdf-autotable");
        const document = new jsPDF({ orientation: "landscape" });
        document.setFontSize(16);
        document.text("Applications Export Report", 10, 14);
        document.setFontSize(9);
        document.text(`Session: ${this.selectedSessionLabel}`, 10, 20);
        document.text(`Generated: ${new Date().toLocaleDateString()}`, 10, 25);
        document.autoTable({
          head: [
            [
              "Application",
              "Name",
              "Email",
              "Phone",
              "Programme",
              "Session",
              "Status",
              "Stage",
              "Clearance",
              "Submitted",
            ],
          ],
          body: applications.map((application) =>
            this.exportRow(application).slice(0, 10)
          ),
          startY: 30,
          styles: { fontSize: 7 },
          headStyles: { fillColor: [33, 37, 41] },
        });
        document.save(
          `applications_${new Date().toISOString().slice(0, 10)}.pdf`
        );
        await Swal.fire(
          "Success",
          `Exported ${applications.length} applications`,
          "success"
        );
      } catch (error) {
        this.showError(error, "Failed to export applications");
      } finally {
        this.exporting = false;
      }
    },
    changePage(page) {
      if (page >= 1 && page <= this.pagination.totalPages) {
        this.fetchApplications(page);
      }
    },
    showError(error, fallback) {
      Swal.fire(
        "Error",
        error.response?.data?.message || error.message || fallback,
        "error"
      );
    },
  },
};
</script>

<template>
  <div class="container py-4 text-start">
    <h4 class="fw-bold">Applications Management</h4>
    <p class="text-muted">Review and manage student applications</p>
    <hr />

    <div class="row mb-4 g-3">
      <div class="col-md-7">
        <div class="input-group">
          <span class="input-group-text bg-white"
            ><i class="bi bi-search"></i
          ></span>
          <input
            v-model="searchQuery"
            type="search"
            class="form-control border-start-0"
            placeholder="Search by name, email, phone, or application number"
            @keyup.enter="fetchApplications(1)"
          />
        </div>
      </div>
      <div class="col-md-5 d-inline-flex justify-content-md-end gap-2">
        <div class="dropdown">
          <button
            class="btn btn-sm btn-outline-secondary"
            type="button"
            data-bs-toggle="dropdown"
          >
            <i class="bi bi-funnel me-1"></i> Filters
          </button>
          <div
            class="dropdown-menu dropdown-menu-end p-3"
            style="min-width: 320px"
            @click.stop
          >
            <div class="mb-2 d-flex justify-content-between align-items-center">
              <span class="fw-semibold small">Advanced Filter</span>
              <button
                class="btn btn-link btn-sm p-0"
                type="button"
                @click="clearFilters"
              >
                clear all
              </button>
            </div>
            <div class="mb-2">
              <label class="small text-muted mb-1 d-block">
                Applied Date Range
              </label>
              <div class="row g-2">
                <div class="col-6">
                  <input
                    v-model="filters.dateRange.start"
                    type="date"
                    class="form-control form-control-sm"
                  />
                </div>
                <div class="col-6">
                  <input
                    v-model="filters.dateRange.end"
                    type="date"
                    class="form-control form-control-sm"
                  />
                </div>
              </div>
            </div>
            <div class="mb-2">
              <label class="small text-muted mb-1 d-block"
                >Program Offering</label
              >
              <select
                v-model="filters.program"
                class="form-select form-select-sm"
              >
                <option value="">All Offerings</option>
                <option
                  v-for="offering in availableProgramOfferings"
                  :key="offering._id"
                  :value="offering._id"
                >
                  {{ offering.name }}
                </option>
              </select>
            </div>
            <div class="mb-2">
              <label class="small text-muted mb-1 d-block">Status</label>
              <select
                v-model="filters.status"
                class="form-select form-select-sm"
              >
                <option value="">All Statuses</option>
                <option
                  v-for="status in statusOptions"
                  :key="status.value"
                  :value="status.value"
                >
                  {{ status.label }}
                </option>
              </select>
            </div>
            <div>
              <label class="small text-muted mb-1 d-block">Stage</label>
              <select
                v-model="filters.stage"
                class="form-select form-select-sm"
              >
                <option value="">All Stages</option>
                <option
                  v-for="stage in stageOptions"
                  :key="stage.value"
                  :value="stage.value"
                >
                  {{ stage.label }}
                </option>
              </select>
            </div>
          </div>
        </div>

        <div class="dropdown">
          <button
            class="btn btn-sm btn-dark"
            type="button"
            data-bs-toggle="dropdown"
            :disabled="exporting"
          >
            <span
              v-if="exporting"
              class="spinner-border spinner-border-sm me-1"
            ></span>
            <i v-else class="bi bi-download me-1"></i> Export
          </button>
          <ul class="dropdown-menu dropdown-menu-end">
            <li>
              <button class="dropdown-item" type="button" @click="exportAsCSV">
                <i class="bi bi-filetype-csv me-2"></i>Export CSV
              </button>
            </li>
            <li>
              <button class="dropdown-item" type="button" @click="exportAsPDF">
                <i class="bi bi-filetype-pdf me-2"></i>Export PDF
              </button>
            </li>
          </ul>
        </div>

        <button
          class="btn btn-sm btn-primary mb-2"
          type="button"
          :disabled="refreshing || loading"
          @click="refreshApplications"
        >
          <i
            class="bi bi-arrow-clockwise me-1"
            :class="{ spin: refreshing }"
          ></i
          >Refresh
        </button>
      </div>
    </div>

    <div class="card border-0 shadow-sm text-center">
      <div
        class="card-header bg-white d-flex justify-content-between align-items-center gap-3"
      >
        <span class="small text-muted">Select academic session</span>
        <select
          v-model="filters.session"
          class="form-select form-select-sm session-select"
        >
          <option value="">All Sessions</option>
          <option
            v-for="session in sortedAcademicSessions"
            :key="session._id"
            :value="session._id"
          >
            {{ session.sessionYear }}
          </option>
        </select>
      </div>
      <div class="card-body p-0 table-responsive">
        <table class="table mb-0 align-middle">
          <thead>
            <tr class="table-dark">
              <th class="text-start">#</th>
              <th class="text-start">Applicant</th>
              <th class="text-start">Contact</th>
              <th class="text-start">Program</th>
              <th>Status</th>
              <th>Applied</th>
              <th>Last Action</th>
              <th>Stage</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <template v-if="loading">
              <tr v-for="number in 5" :key="`loading-${number}`">
                <td colspan="9" class="py-2">
                  <div class="placeholder-glow">
                    <span class="placeholder col-12"></span>
                  </div>
                </td>
              </tr>
            </template>
            <tr v-else-if="!applications.length">
              <td colspan="9" class="py-4">
                <h6 class="mb-1">No applications found</h6>
                <p class="text-muted mb-2">
                  There are currently no applicant records for this filter.
                </p>
                <button
                  class="btn btn-outline-primary btn-sm"
                  type="button"
                  @click="refreshApplications"
                >
                  <i class="bi bi-arrow-clockwise me-1"></i>Refresh
                </button>
              </td>
            </tr>
            <tr
              v-for="application in applications"
              v-else
              :key="application._id"
            >
              <td class="text-start">
                {{ application.applicationNumber || "N/A" }}
              </td>
              <td class="text-start text-capitalize">
                {{ fullName(application) }}
              </td>
              <td class="text-start">
                <div>{{ application.userId?.email || "N/A" }}</div>
                <small class="text-muted">{{
                  application.userId?.phone || "N/A"
                }}</small>
              </td>
              <td class="text-start">
                {{ formatProgramOffering(application.programOfferingId) }}
              </td>
              <td>
                <span
                  :class="`badge text-bg-${getStatusBadgeColor(
                    application.status
                  )}`"
                >
                  {{ getStatusLabel(application.status) }}
                </span>
              </td>
              <td>
                {{
                  formatDate(
                    application.submittedAt || application.registrationDate
                  )
                }}
              </td>
              <td>{{ formatDate(application.updatedAt) }}</td>
              <td>
                <span
                  :class="`badge text-bg-${getCurrentStageBadgeColor(
                    application
                  )}`"
                >
                  {{ getCurrentStage(application) }}
                </span>
              </td>
              <td>
                <div class="btn-group" role="group">
                  <button
                    class="btn btn-sm btn-outline-dark"
                    type="button"
                    title="View application details"
                    @click="viewApplicationDetails(application)"
                  >
                    <i class="bi bi-eye"></i>
                  </button>
                  <div class="dropdown">
                    <button
                      class="btn btn-sm btn-outline-dark dropdown-toggle no-arrow rounded-start-0 border-start-0"
                      type="button"
                      data-bs-toggle="dropdown"
                      title="More actions"
                    >
                      <i class="bi bi-three-dots-vertical"></i>
                    </button>
                    <ul class="dropdown-menu dropdown-menu-end">
                      <li v-if="canEditApplication(application)">
                        <button
                          class="dropdown-item"
                          type="button"
                          @click="openApplicationAction(application, 'edit')"
                        >
                          <i class="bi bi-pencil-square me-2"></i>Edit
                          application
                        </button>
                      </li>
                      <li v-if="canChangeProgramme(application)">
                        <button
                          class="dropdown-item"
                          type="button"
                          @click="
                            openApplicationAction(application, 'programme')
                          "
                        >
                          <i class="bi bi-arrow-left-right me-2"></i>Change
                          programme
                        </button>
                      </li>
                      <li v-if="canManageDocuments(application)">
                        <button
                          class="dropdown-item"
                          type="button"
                          @click="
                            openApplicationAction(application, 'documents')
                          "
                        >
                          <i class="bi bi-files me-2"></i>Manage documents
                        </button>
                      </li>
                      <li
                        v-if="
                          canEditApplication(application) ||
                          canChangeProgramme(application) ||
                          canManageDocuments(application)
                        "
                      >
                        <hr class="dropdown-divider" />
                      </li>
                      <li v-if="canAdmit(application)">
                        <button
                          class="dropdown-item text-success"
                          type="button"
                          @click="admitApplicant(application)"
                        >
                          <i class="bi bi-check-circle-fill me-2"></i>Admit
                        </button>
                      </li>
                      <li v-if="canReject(application)">
                        <button
                          class="dropdown-item text-danger"
                          type="button"
                          @click="rejectApplicant(application)"
                        >
                          <i class="bi bi-x-circle-fill me-2"></i>Reject
                        </button>
                      </li>
                      <li v-if="canReopen(application)">
                        <button
                          class="dropdown-item text-primary"
                          type="button"
                          @click="reopenApplication(application)"
                        >
                          <i class="bi bi-arrow-counterclockwise me-2"></i
                          >Reopen
                        </button>
                      </li>
                      <li v-if="canDecideClearance(application)">
                        <button
                          class="dropdown-item text-primary"
                          type="button"
                          @click="clearApplicant(application)"
                        >
                          <i class="bi bi-check2-circle me-2"></i>Clear
                        </button>
                      </li>
                      <li v-if="canDecideClearance(application)">
                        <button
                          class="dropdown-item text-danger"
                          type="button"
                          @click="denyClearance(application)"
                        >
                          <i class="bi bi-x-octagon me-2"></i>Deny Clearance
                        </button>
                      </li>
                      <li v-if="!hasAnyRowActions(application)">
                        <span class="dropdown-item text-muted"
                          >No actions available</span
                        >
                      </li>
                    </ul>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div
        class="card-footer text-muted d-flex justify-content-between align-items-center gap-3 flex-wrap"
      >
        <div class="text-muted d-inline-flex align-items-center gap-2">
          Per Page:
          <select
            v-model.number="pagination.itemsPerPage"
            class="form-select form-select-sm per-page-select"
          >
            <option :value="10">10</option>
            <option :value="25">25</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
          </select>
        </div>
        <nav class="p-0" aria-label="Applications pagination">
          <ul class="pagination justify-content-center mb-0">
            <li
              class="page-item"
              :class="{ disabled: pagination.currentPage === 1 }"
            >
              <button
                class="page-link"
                type="button"
                @click="changePage(pagination.currentPage - 1)"
              >
                &laquo;
              </button>
            </li>
            <li
              v-for="page in pageRange"
              :key="page"
              class="page-item"
              :class="{ active: page === pagination.currentPage }"
            >
              <button class="page-link" type="button" @click="changePage(page)">
                {{ page }}
              </button>
            </li>
            <li
              class="page-item"
              :class="{
                disabled: pagination.currentPage === pagination.totalPages,
              }"
            >
              <button
                class="page-link"
                type="button"
                @click="changePage(pagination.currentPage + 1)"
              >
                &raquo;
              </button>
            </li>
          </ul>
        </nav>
        <span
          >Showing {{ paginationInfo.start }}-{{ paginationInfo.end }} of
          {{ paginationInfo.total }} applications</span
        >
      </div>
    </div>

    <div
      v-if="detailsLoading || actionLoading"
      class="details-backdrop"
      role="status"
    >
      <div class="spinner-border text-light">
        <span class="visually-hidden">Loading</span>
      </div>
    </div>

    <div
      v-if="selectedApplication"
      class="details-backdrop"
      role="dialog"
      aria-modal="true"
      @click.self="closeDetails"
    >
      <div class="details-dialog bg-white rounded shadow-lg">
        <div
          class="d-flex align-items-start justify-content-between border-bottom p-3"
        >
          <div>
            <h5 class="fw-bold mb-1">
              <i class="bi bi-person-vcard-fill me-2"></i>
              {{ fullName(selectedApplication) }}
            </h5>
            <small class="text-muted">
              {{ selectedApplication.applicationNumber }}
            </small>
          </div>
          <button
            class="btn-close"
            type="button"
            aria-label="Close"
            @click="closeDetails"
          ></button>
        </div>
        <div class="details-body p-4">
          <div class="row g-3 mb-3">
            <div class="col-md-6">
              <strong>Entry Session:</strong>
              <span class="text-muted">
                {{ selectedApplication.entrySessionId?.sessionYear || "N/A" }}
              </span>
            </div>
            <div class="col-md-6">
              <strong>Program:</strong>
              <span class="text-muted">
                {{
                  formatProgramOffering(selectedApplication.programOfferingId)
                }}
              </span>
            </div>
            <div class="col-md-6">
              <strong>Email:</strong>
              <span class="text-muted">
                {{ selectedApplication.userId?.email || "N/A" }}
              </span>
            </div>
            <div class="col-md-6">
              <strong>Phone:</strong>
              <span class="text-muted">
                {{ selectedApplication.userId?.phone || "N/A" }}
              </span>
            </div>
            <div class="col-md-6">
              <strong>Status:</strong>
              <span
                :class="`badge text-bg-${getStatusBadgeColor(
                  selectedApplication.status
                )}`"
              >
                {{ getStatusLabel(selectedApplication.status) }}
              </span>
            </div>
            <div class="col-md-6">
              <strong>Clearance:</strong>
              <span class="text-muted">
                {{ getClearanceLabel(selectedApplication.clearance?.status) }}
              </span>
            </div>
          </div>

          <section
            v-if="hasAnyRowActions(selectedApplication)"
            class="detail-section mb-0"
          >
            <h6 class="fw-bold">Application Actions</h6>
            <div class="d-flex flex-wrap gap-2">
              <button
                v-if="canEditApplication(selectedApplication)"
                class="btn btn-sm btn-outline-primary"
                type="button"
                @click="openApplicationAction(selectedApplication, 'edit')"
              >
                <i class="bi bi-pencil-square me-1"></i>Edit application
              </button>
              <button
                v-if="canChangeProgramme(selectedApplication)"
                class="btn btn-sm btn-outline-primary"
                type="button"
                @click="openApplicationAction(selectedApplication, 'programme')"
              >
                <i class="bi bi-arrow-left-right me-1"></i>Change programme
              </button>
              <button
                v-if="canManageDocuments(selectedApplication)"
                class="btn btn-sm btn-outline-primary"
                type="button"
                @click="openApplicationAction(selectedApplication, 'documents')"
              >
                <i class="bi bi-files me-1"></i>Manage documents
              </button>
              <button
                v-if="canAdmit(selectedApplication)"
                class="btn btn-sm btn-success"
                type="button"
                @click="admitApplicant(selectedApplication)"
              >
                <i class="bi bi-check-circle me-1"></i>Admit
              </button>
              <button
                v-if="canReject(selectedApplication)"
                class="btn btn-sm btn-danger"
                type="button"
                @click="rejectApplicant(selectedApplication)"
              >
                <i class="bi bi-x-circle me-1"></i>Reject
              </button>
              <button
                v-if="canReopen(selectedApplication)"
                class="btn btn-sm btn-primary"
                type="button"
                @click="reopenApplication(selectedApplication)"
              >
                <i class="bi bi-arrow-counterclockwise me-1"></i>Reopen
              </button>
              <button
                v-if="canDecideClearance(selectedApplication)"
                class="btn btn-sm btn-success"
                type="button"
                @click="clearApplicant(selectedApplication)"
              >
                <i class="bi bi-check2-circle me-1"></i>Clear
              </button>
              <button
                v-if="canDecideClearance(selectedApplication)"
                class="btn btn-sm btn-outline-danger"
                type="button"
                @click="denyClearance(selectedApplication)"
              >
                <i class="bi bi-x-octagon me-1"></i>Deny Clearance
              </button>
            </div>
          </section>

          <section class="detail-section">
            <h6 class="fw-bold">
              <i class="bi bi-diagram-3 me-2"></i>Application Progress
            </h6>
            <div class="progress mb-2" style="height: 10px">
              <div
                class="progress-bar bg-success"
                :style="{ width: `${progressPercentage}%` }"
              ></div>
            </div>
            <p class="small text-muted">
              {{ progressStages.filter((stage) => stage.completed).length }} of
              {{ progressStages.length }} stages completed ({{
                progressPercentage
              }}%)
            </p>
            <div class="row row-cols-1 row-cols-sm-2">
              <div
                v-for="stage in progressStages"
                :key="stage.label"
                class="col mb-1"
              >
                <i
                  :class="
                    stage.completed
                      ? 'bi bi-check-circle-fill text-success'
                      : 'bi bi-circle text-muted'
                  "
                  class="me-2"
                ></i
                >{{ stage.label }}
              </div>
            </div>
          </section>

          <section class="detail-section">
            <h6 class="fw-bold">
              <i class="bi bi-person-lines-fill me-2"></i>Biodata
            </h6>
            <div class="row g-2">
              <div class="col-md-6">
                <strong>Gender:</strong>
                <span class="text-muted text-capitalize">{{
                  selectedApplication.gender || "N/A"
                }}</span>
              </div>
              <div class="col-md-6">
                <strong>Date of Birth:</strong>
                <span class="text-muted">{{
                  formatDate(selectedApplication.userId?.dateOfBirth)
                }}</span>
              </div>
              <div class="col-md-6">
                <strong>Nationality:</strong>
                <span class="text-muted">{{
                  selectedApplication.nationality || "N/A"
                }}</span>
              </div>
              <div class="col-md-6">
                <strong>State / LGA:</strong>
                <span class="text-muted">{{
                  [selectedApplication.stateOfOrigin, selectedApplication.lga]
                    .filter(Boolean)
                    .join(", ") || "N/A"
                }}</span>
              </div>
              <div class="col-md-6">
                <strong>Place of Birth:</strong>
                <span class="text-muted">{{
                  selectedApplication.placeOfBirth || "N/A"
                }}</span>
              </div>
              <div class="col-md-6">
                <strong>Marital Status:</strong>
                <span class="text-muted text-capitalize">{{
                  selectedApplication.maritalStatus || "N/A"
                }}</span>
              </div>
              <div class="col-12">
                <strong>Address:</strong>
                <span class="text-muted">{{
                  formatAddress(selectedApplication.permanentAddress)
                }}</span>
              </div>
              <div class="col-12">
                <strong>O-Level Status:</strong>
                <span class="text-muted">{{
                  selectedApplication.oLevelResultStatus === "awaiting_result"
                    ? "Awaiting Result"
                    : "Completed"
                }}</span>
              </div>
            </div>
          </section>

          <section class="detail-section">
            <h6 class="fw-bold">
              <i class="bi bi-file-earmark-arrow-up me-2"></i>Documents
            </h6>
            <p
              v-if="!selectedApplication.documents?.length"
              class="text-muted small mb-0"
            >
              No uploaded documents found.
            </p>
            <div
              v-for="document in selectedApplication.documents"
              :key="document._id"
              class="d-flex justify-content-between align-items-center border rounded p-2 mb-2"
            >
              <div>
                <strong>{{ documentLabel(document) }}</strong
                ><small class="d-block text-muted"
                  >{{ document.originalName }} · {{ document.status }}</small
                >
              </div>
              <button
                class="btn btn-sm btn-outline-primary"
                type="button"
                :disabled="documentLoadingId === document._id"
                @click="openDocument(document)"
              >
                <span
                  v-if="documentLoadingId === document._id"
                  class="spinner-border spinner-border-sm"
                ></span
                ><span v-else
                  ><i class="bi bi-box-arrow-up-right me-1"></i>Open</span
                >
              </button>
            </div>
          </section>

          <section class="detail-section">
            <h6 class="fw-bold"><i class="bi bi-receipt me-2"></i>Payments</h6>
            <p
              v-if="!selectedApplication.payments?.length"
              class="text-muted small mb-0"
            >
              No payment records found.
            </p>
            <div
              v-for="payment in selectedApplication.payments"
              :key="payment._id"
              class="d-flex justify-content-between border-bottom py-2 gap-3"
            >
              <div>
                <strong>{{ paymentName(payment) }}</strong
                ><small class="d-block text-muted"
                  >{{ payment.paymentRef }} ·
                  {{
                    formatDate(payment.paymentDate || payment.createdAt)
                  }}</small
                >
              </div>
              <div class="text-end">
                <strong
                  >₦{{ Number(payment.amount || 0).toLocaleString() }}</strong
                ><span
                  class="badge ms-2"
                  :class="
                    payment.status === 'successful'
                      ? 'text-bg-success'
                      : 'text-bg-secondary'
                  "
                  >{{ payment.status }}</span
                >
              </div>
            </div>
          </section>

          <section class="detail-section">
            <h6 class="fw-bold">
              <i class="bi bi-people me-2"></i>Guardian / Next of Kin
            </h6>
            <div class="row g-2">
              <div class="col-md-6">
                <strong>Guardian:</strong>
                <span class="text-muted">{{
                  selectedApplication.guardian?.name || "N/A"
                }}</span>
              </div>
              <div class="col-md-6">
                <strong>Guardian Phone:</strong>
                <span class="text-muted">{{
                  selectedApplication.guardian?.phone || "N/A"
                }}</span>
              </div>
              <div class="col-md-6">
                <strong>Next of Kin:</strong>
                <span class="text-muted">{{
                  selectedApplication.nextOfKin?.name || "N/A"
                }}</span>
              </div>
              <div class="col-md-6">
                <strong>Kin Phone:</strong>
                <span class="text-muted">{{
                  selectedApplication.nextOfKin?.phone || "N/A"
                }}</span>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>

    <ApplicationEditModal
      v-if="actionMode === 'edit' && actionApplication"
      :application="actionApplication"
      @close="closeApplicationAction"
      @saved="handleApplicationActionSaved"
    />
    <ApplicationProgrammeModal
      v-if="actionMode === 'programme' && actionApplication"
      :application="actionApplication"
      :offerings="programOfferings"
      @close="closeApplicationAction"
      @saved="handleApplicationActionSaved"
    />
    <ApplicationDocumentsModal
      v-if="actionMode === 'documents' && actionApplication"
      :application="actionApplication"
      @close="closeApplicationAction"
      @saved="handleApplicationActionSaved"
    />
  </div>
</template>

<style scoped>
.no-arrow::after {
  display: none !important;
}
.session-select {
  max-width: 220px;
}
.per-page-select {
  width: 80px;
}
.details-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1060;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.details-dialog {
  width: min(900px, 100%);
  max-height: 92vh;
  overflow: hidden;
}
.details-body {
  max-height: calc(92vh - 78px);
  overflow-y: auto;
}
.detail-section {
  border-top: 1px solid var(--bs-border-color);
  padding-top: 1rem;
  margin-top: 1rem;
  margin-bottom: 1rem;
}
.spin {
  display: inline-block;
  animation: spin 1s linear infinite;
}
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
